<script>
import axios from 'axios';
import { commonService } from '@/helpers/common.service';

export default {
    name: "MdmSettingsSection",
    props: {
        isApprover: { type: Boolean, default: false }
    },
    data() {
        return {
            loading: false,
            saving: false,
            settings: [],
            isEditing: false,
            isCreating: false,
            editingId: null,
            editForm: {
                type: 1, // Default to Send Warning
                template_type: '',
                name: '',
                message_title: '',
                message_body: '',
                footnote: '',
                phone_number: '',
                duration: 10,
                status: 1
            }
        };
    },
    methods: {
        getTemplates() {
            this.loading = true;
            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
            axios.get(process.env.VUE_APP_API_BASEURL + 'mdm/templates')
                .then(response => {
                    if (response && response.data && response.data.data) {
                        this.settings = response.data.data;
                    } else if (response && Array.isArray(response.data)) {
                        this.settings = response.data;
                    }
                })
                .catch(error => {
                    console.error("Error fetching templates:", error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        editSetting(setting) {
            this.isEditing = true;
            this.isCreating = false;
            this.editingId = setting.id;
            this.editForm = { ...setting };
            if (!this.editForm.name && this.editForm.message_title) {
                this.editForm.name = this.editForm.message_title;
            }
            this.editForm.status = setting.status == 1 || setting.status === 'active' ? 1 : 0;
            // Map legacy message_template to message_body if missing in API, just in case
            if (!this.editForm.message_body && this.editForm.message_template) {
                this.editForm.message_body = this.editForm.message_template;
            }
        },
        createTemplate() {
            this.isCreating = true;
            this.isEditing = false;
            this.editingId = null;
            this.editForm = { 
                type: 1, // Send Warning
                template_type: 'Send Warning',
                name: '', 
                message_body: '', 
                footnote: '',
                phone_number: '',
                duration: 10, 
                status: 1 
            };
        },
        saveSetting() {
            this.saving = true;
            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
            
            // NOTE: API info for "Add" will be provided later.
            // Using PUT for update (existing), POST for create (new).
            const isUpdate = this.isEditing && this.editingId;
            const url = isUpdate 
                ? process.env.VUE_APP_API_BASEURL + 'mdm/templates/' + this.editingId
                : process.env.VUE_APP_API_BASEURL + 'mdm/templates';
            
            const payload = {
                _method: isUpdate ? 'PUT' : 'POST',
                type: this.editForm.type,
                status: this.editForm.status,
                name: this.editForm.name || '',
                message_title: null, // As requested, let it be null
                message_body: this.editForm.message_body || '',
                footnote: this.editForm.footnote || '',
                phone_number: this.editForm.phone_number || '',
                duration: this.editForm.duration || ''
            };

            axios.post(url, payload)
                .then(() => {
                    const actionPath = isUpdate ? 'updated' : 'created';
                    commonService.swalPrompt(true, `Template ${actionPath} successfully!`, 'Success', 2000);
                    this.getTemplates();
                    this.cancelEdit();
                })
                .catch(error => {
                    commonService.swalPrompt(false, error.response?.data?.message || `Error ${isUpdate ? 'updating' : 'creating'} template`, 'Action Failed');
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        cancelEdit() {
            this.editingId = null;
            this.isEditing = false;
            this.isCreating = false;
            this.editForm = { type: 1, template_type: '', message_title: '', message_body: '', footnote: '', phone_number: '', duration: 0, status: 1 };
        },
        getTypeClass(type) {
            if (!type) return 'bg-light text-dark';
            const map = { 
                1: 'bg-warning-subtle text-warning', // Send Warning
                2: 'bg-danger-subtle text-danger',  // Enable Lost Mode
                3: 'bg-success-subtle text-success', // Disable Lost Mode
                'warning': 'bg-warning-subtle text-warning',
                'lock': 'bg-danger-subtle text-danger',
                'unlock': 'bg-success-subtle text-success'
            };
            const key = typeof type === 'string' ? type.toLowerCase() : type;
            return map[key] || 'bg-light text-dark';
        },
        getTypeLabel(setting) {
            return setting.type_description || setting.template_type || setting.type || 'UNKNOWN';
        }
    },
    mounted() {
        this.getTemplates();
    }
};
</script>

<template>
    <div class="p-1">
        <div class="row">
            <!-- Settings List -->
            <div :class="(isEditing || isCreating) ? 'col-md-7' : 'col-md-12'">
                <div class="d-flex align-items-center justify-content-between mb-3">
                    <h6 class="fw-semibold mb-0">
                        <i class="ri-settings-line me-1 align-bottom"></i> Configuration Templates
                    </h6>
                    <button class="btn btn-primary btn-sm" @click="createTemplate" v-if="!isCreating">
                        <i class="ri-add-line align-bottom me-1"></i> Add New Template
                    </button>
                </div>
                
                <div class="table-responsive border rounded shadow-sm bg-white">
                    <table class="table table-hover align-middle mb-0">
                        <thead class="table-light text-muted fs-11 text-uppercase">
                            <tr>
                                <th style="width: 100px;">Action Type</th>
                                <th>Template Name</th>
                                <th>Message Body</th>
                                <th>Footnote</th>
                                <th>Phone Contact</th>
                                <th style="width: 100px;">Duration (Sec)</th>
                                <th style="width: 80px;">Status</th>
                                <th style="width: 80px;">Action</th>
                            </tr>
                        </thead>
                        <tbody class="fs-13">
                            <tr v-if="loading">
                                <td colspan="6" class="text-center py-4">
                                    <div class="spinner-border text-primary spinner-border-sm" role="status"></div>
                                    <span class="ms-2">Loading templates...</span>
                                </td>
                            </tr>
                            <tr v-else-if="settings.length === 0">
                                <td colspan="6" class="text-center py-4 text-muted">No configuration templates found</td>
                            </tr>
                            <tr v-else v-for="s in settings" :key="s.id" :class="{ 'table-active': editingId === s.id }">
                                <td>
                                    <span :class="['badge px-2 py-1', getTypeClass(s.type)]">{{ getTypeLabel(s).toUpperCase() }}</span>
                                </td>
                                <td>{{ s.name || s.message_title || '---' }}</td>
                                <td class="text-wrap" style="max-width: 300px;">{{ s.message_body || '---' }}</td>
                                <td class="text-wrap" style="max-width: 200px;">{{ s.footnote || '---' }}</td>
                                <td>{{ s.phone_number || '---' }}</td>
                                <td class="text-center">{{ s.duration > 0 ? s.duration : 'N/A' }}</td>
                                <td>
                                    <span :class="['badge rounded-pill', s.status == 1 ? 'bg-success' : 'bg-danger']">
                                        {{ s.status_description || (s.status == 1 ? 'Active' : 'Inactive') }}
                                    </span>
                                </td>
                                <td>
                                    <button class="btn btn-soft-primary btn-sm btn-icon waves-effect shadow-none" @click="editSetting(s)">
                                        <i class="ri-edit-2-line"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Form (Create/Edit) -->
            <div v-if="isEditing || isCreating" class="col-md-5 animated fadeIn">
                <div class="card border shadow-sm">
                    <div class="card-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                        <h6 class="card-title mb-0 text-white fs-13">
                            {{ isCreating ? 'Create New' : 'Edit ' + getTypeLabel(editForm) }} Template
                        </h6>
                        <button type="button" class="btn-close btn-close-white" @click="cancelEdit"></button>
                    </div>
                    <div class="card-body">
                        <div class="mb-3">
                            <label class="form-label fs-12 text-muted">Action Type <span class="text-danger">*</span></label>
                            <select v-model="editForm.type" class="form-select fs-13">
                                <option :value="1">Send Warning</option>
                                <option :value="2">Enable Lost Mode</option>
                            </select>
                        </div>
                        <div class="mb-3">
                            <label class="form-label fs-12 text-muted">Template Name <span class="text-danger">*</span></label>
                            <input v-model="editForm.name" type="text" class="form-control fs-13" placeholder="Enter name (e.g. Send Warning Template)">
                        </div>
                        <div class="mb-3">
                            <label class="form-label fs-12 text-muted">Message Body <span class="text-danger">*</span></label>
                            <textarea v-model="editForm.message_body" class="form-control fs-13" rows="4" placeholder="Enter message body content..."></textarea>
                        </div>
                        <div class="mb-3">
                            <label class="form-label fs-12 text-muted">Footnote <span class="text-danger">*</span></label>
                            <textarea v-model="editForm.footnote" class="form-control fs-13" rows="2" placeholder="e.g. Kindly call our customer service..." maxlength="255"></textarea>
                        </div>
                        <div class="mb-3">
                            <label class="form-label fs-12 text-muted">Support Phone Number <span class="text-danger">*</span></label>
                            <input v-model="editForm.phone_number" type="tel" class="form-control fs-13" placeholder="e.g. 03-12345678" maxlength="20" @input="editForm.phone_number = editForm.phone_number.replace(/[^0-9+\-() ]/g, '')">
                        </div>
                        <div class="row g-3 mb-4">
                            <div class="col-6">
                                <label class="form-label fs-12 text-muted">Duration (Seconds)</label>
                                <input v-model.number="editForm.duration" type="number" class="form-control fs-13">
                            </div>
                            <div class="col-6">
                                <label class="form-label fs-12 text-muted">Status</label>
                                <div class="form-check form-switch mt-1">
                                    <input class="form-check-input" type="checkbox" v-model="editForm.status" :true-value="1" :false-value="0">
                                    <label class="form-check-label fs-12 ms-1">{{ editForm.status == 1 ? 'Active' : 'Inactive' }}</label>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex gap-2">
                            <button class="btn btn-primary w-100" :disabled="saving || !editForm.name || !editForm.message_body || !editForm.footnote || !editForm.phone_number" @click="saveSetting">
                                <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                {{ isCreating ? 'Create Template' : 'Save Changes' }}
                            </button>
                            <button class="btn btn-light w-100" @click="cancelEdit">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.animated { animation-duration: 0.3s; }
.fadeIn { animation-name: fadeIn; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.italic { font-style: italic; }
</style>
